import { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Phone, MessageCircle, MapPin, Camera, CheckCircle2, Loader2, Play, Square, Trash2, Upload,
} from "lucide-react";
import { toast } from "sonner";
import { companion, companionFileUrl } from "@/context/CompanionAuthContext";
import { useCompanionAuth } from "@/context/CompanionAuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  STATUS_LABELS, STATUS_COLORS, PHOTO_LABELS, GALLERY_CATS, formatDate, formatDateTime,
} from "./utils";

const STATUSES = ["scheduled", "in_progress", "on_hold", "completed", "invoiced"];

const CompanionJobDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useCompanionAuth();
  const isAdmin = user?.role === "admin";

  const [job, setJob] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Upload state
  const [label, setLabel] = useState("in_progress");
  const [caption, setCaption] = useState("");
  const [publishToGallery, setPublishToGallery] = useState(false);
  const [galleryCategory, setGalleryCategory] = useState("render-insulation");
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [j, p, c] = await Promise.all([
        companion.get(`/jobs/${id}`).then((r) => r.data),
        companion.get(`/jobs/${id}/photos`).then((r) => r.data),
        companion.get("/time/current").then((r) => r.data),
      ]);
      setJob(j); setPhotos(p); setCurrent(c);
    } catch {
      toast.error("Could not load job.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const updateStatus = async (status) => {
    setBusy(true);
    try {
      const { data } = await companion.patch(`/jobs/${id}`, { status });
      setJob(data);
      toast.success(`Status: ${STATUS_LABELS[status]}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update.");
    } finally { setBusy(false); }
  };

  const clockIn = async () => {
    setBusy(true);
    try {
      const { data } = await companion.post("/time/start", { job_id: id });
      setCurrent(data);
      toast.success("Clocked in to this job.");
    } catch { toast.error("Couldn't clock in."); }
    finally { setBusy(false); }
  };

  const clockOut = async () => {
    setBusy(true);
    try {
      await companion.post("/time/stop", {});
      setCurrent(null);
      toast.success("Clocked out.");
    } catch { toast.error("Couldn't clock out."); }
    finally { setBusy(false); }
  };

  const onUpload = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Please choose or take a photo."); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("label", label);
    if (caption) fd.append("caption", caption);
    fd.append("publish_to_gallery", publishToGallery ? "true" : "false");
    if (publishToGallery) fd.append("gallery_category", galleryCategory);
    setUploading(true);
    try {
      const { data } = await companion.post(`/jobs/${id}/photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPhotos((p) => [data, ...p]);
      setCaption("");
      if (fileRef.current) fileRef.current.value = "";
      toast.success("Photo uploaded." + (publishToGallery ? " Also published to public gallery." : ""));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally { setUploading(false); }
  };

  const deletePhoto = async (pid) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await companion.delete(`/jobs/${id}/photos/${pid}`);
      setPhotos((p) => p.filter((x) => x.id !== pid));
    } catch { toast.error("Couldn't delete."); }
  };

  const deleteJob = async () => {
    if (!window.confirm("Delete this job? This cannot be undone.")) return;
    try {
      await companion.delete(`/jobs/${id}`);
      toast.success("Job deleted.");
      navigate("/companion/jobs", { replace: true });
    } catch { toast.error("Couldn't delete."); }
  };

  if (loading || !job) {
    return <div className="grid place-items-center py-24"><Loader2 className="h-7 w-7 animate-spin text-crest-green" /></div>;
  }

  const phoneHref = job.customer_phone ? `tel:${job.customer_phone.replace(/\s+/g, "")}` : null;
  const waHref = job.customer_phone
    ? `https://wa.me/${job.customer_phone.replace(/[^0-9]/g, "").replace(/^0/, "44")}`
    : null;
  const mapsHref = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${job.address} ${job.postcode || ""}`)}`;
  const onThisJob = current?.job_id === id;

  return (
    <div className="px-4 py-5 space-y-5" data-testid={`companion-job-detail-${id}`}>
      <Link to="/companion/jobs" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-crest-green font-medium" data-testid="back-to-jobs">
        <ArrowLeft className="h-3.5 w-3.5" /> All jobs
      </Link>

      <header>
        <div className="flex items-center gap-2">
          <span className={`text-[11px] px-2 py-0.5 rounded ${STATUS_COLORS[job.status]}`}>{STATUS_LABELS[job.status]}</span>
          {job.service_type && <span className="text-[11px] text-crest-gold">{job.service_type}</span>}
        </div>
        <h1 className="mt-2 font-display text-2xl font-bold text-crest-navy">{job.customer_name}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          <MapPin className="inline h-3.5 w-3.5 mr-1" />{job.address}{job.postcode ? `, ${job.postcode}` : ""}
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          {formatDate(job.scheduled_date)}{job.scheduled_time_start ? ` · ${job.scheduled_time_start}` : ""}{job.estimated_hours ? ` · ~${job.estimated_hours}h` : ""}
        </p>
      </header>

      {/* Quick actions */}
      <div className="grid grid-cols-3 gap-2">
        {phoneHref && (
          <a href={phoneHref} className="flex flex-col items-center gap-1 rounded-lg border border-border bg-white p-3 text-xs font-medium text-crest-navy active:bg-crest-green-100"
             data-testid="job-call-customer">
            <Phone className="h-5 w-5 text-crest-green" /> Call
          </a>
        )}
        {waHref && (
          <a href={waHref} target="_blank" rel="noreferrer" className="flex flex-col items-center gap-1 rounded-lg border border-border bg-white p-3 text-xs font-medium text-crest-navy active:bg-crest-green-100"
             data-testid="job-whatsapp-customer">
            <MessageCircle className="h-5 w-5 text-[#25D366]" /> WhatsApp
          </a>
        )}
        <a href={mapsHref} target="_blank" rel="noreferrer" className="flex flex-col items-center gap-1 rounded-lg border border-border bg-white p-3 text-xs font-medium text-crest-navy active:bg-crest-green-100"
           data-testid="job-open-maps">
          <MapPin className="h-5 w-5 text-crest-navy" /> Maps
        </a>
      </div>

      {/* Status update */}
      <section className="rounded-lg border border-border bg-white p-4">
        <h2 className="font-display text-sm font-semibold text-crest-navy mb-3">Status</h2>
        <div className="flex flex-wrap gap-1.5">
          {STATUSES.map((s) => (
            <button
              key={s}
              disabled={busy || job.status === s}
              onClick={() => updateStatus(s)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                job.status === s
                  ? "bg-crest-green text-white border-crest-green"
                  : "bg-white border-border text-crest-navy hover:border-crest-green disabled:opacity-50"
              }`}
              data-testid={`status-btn-${s}`}
            >
              {STATUS_LABELS[s]}
            </button>
          ))}
        </div>
      </section>

      {/* Clock in/out for THIS job */}
      <section className="rounded-lg border border-border bg-white p-4">
        <h2 className="font-display text-sm font-semibold text-crest-navy mb-2">Time tracking</h2>
        {current ? (
          onThisJob ? (
            <div className="flex items-center justify-between">
              <div className="text-sm">
                <div className="text-crest-green font-medium">Clocked in to this job</div>
                <div className="text-xs text-muted-foreground">Since {formatDateTime(current.started_at)}</div>
              </div>
              <button onClick={clockOut} disabled={busy}
                      className="inline-flex items-center gap-1.5 rounded-md bg-crest-navy text-white px-4 py-2 text-sm font-semibold disabled:opacity-50"
                      data-testid="job-clock-out">
                <Square className="h-4 w-4" /> Clock out
              </button>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">You're clocked in on another job. Clock out first to start this one.</p>
          )
        ) : (
          <button onClick={clockIn} disabled={busy}
                  className="inline-flex items-center gap-1.5 rounded-md bg-crest-green text-white px-4 py-2 text-sm font-semibold disabled:opacity-50"
                  data-testid="job-clock-in">
            <Play className="h-4 w-4" /> Clock in to this job
          </button>
        )}
      </section>

      {/* Description / notes */}
      <section className="rounded-lg border border-border bg-white p-4">
        <h2 className="font-display text-sm font-semibold text-crest-navy mb-2">Job description</h2>
        <p className="text-sm text-crest-navy/85 whitespace-pre-wrap">{job.description}</p>
        {job.notes && (
          <>
            <h3 className="mt-4 font-display text-xs uppercase tracking-[0.14em] text-crest-gold">Notes</h3>
            <p className="mt-1 text-sm text-crest-navy/85 whitespace-pre-wrap">{job.notes}</p>
          </>
        )}
      </section>

      {/* Photo upload */}
      <section className="rounded-lg border border-border bg-white p-4" data-testid="job-photo-upload">
        <div className="flex items-center gap-2 mb-3">
          <Camera className="h-5 w-5 text-crest-green" />
          <h2 className="font-display text-sm font-semibold text-crest-navy">Add a photo</h2>
        </div>
        <form onSubmit={onUpload} className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <label className="text-xs uppercase tracking-[0.14em] text-crest-navy/70 col-span-2">Label</label>
            {Object.entries(PHOTO_LABELS).map(([k, v]) => (
              <button
                type="button"
                key={k}
                onClick={() => setLabel(k)}
                className={`px-3 py-2 rounded-md border text-xs font-medium transition-colors ${
                  label === k ? "bg-crest-green text-white border-crest-green" : "bg-white border-border text-crest-navy"
                }`}
                data-testid={`photo-label-${k}`}
              >
                {v}
              </button>
            ))}
          </div>
          <Input
            placeholder="Optional caption (e.g. 'EWI started on south elevation')"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            className="h-11"
            data-testid="photo-caption"
          />
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="block w-full h-11 text-sm file:mr-3 file:rounded-md file:border-0 file:bg-crest-green file:px-4 file:py-2 file:text-white file:font-medium file:cursor-pointer"
            data-testid="photo-file"
          />
          {isAdmin && (
            <>
              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={publishToGallery} onChange={(e) => setPublishToGallery(e.target.checked)} data-testid="publish-to-gallery" />
                <span>Also publish this photo to the public website gallery</span>
              </label>
              {publishToGallery && (
                <select
                  value={galleryCategory}
                  onChange={(e) => setGalleryCategory(e.target.value)}
                  className="h-11 w-full rounded-md border border-input bg-white px-3 text-sm"
                  data-testid="publish-gallery-category"
                >
                  {GALLERY_CATS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              )}
            </>
          )}
          <button
            type="submit"
            disabled={uploading}
            data-testid="photo-upload-submit"
            className="w-full h-12 inline-flex items-center justify-center gap-2 rounded-md bg-crest-green text-white font-semibold disabled:opacity-60"
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            Upload photo
          </button>
        </form>
      </section>

      {/* Photo gallery */}
      <section data-testid="job-photo-gallery">
        <h2 className="font-display text-sm font-semibold text-crest-navy mb-2">Job photos · {photos.length}</h2>
        {photos.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border bg-white p-6 text-center text-sm text-muted-foreground">
            No photos yet — be the first.
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {photos.map((p) => (
              <div key={p.id} className="relative group rounded-lg border border-border bg-white overflow-hidden" data-testid={`job-photo-${p.id}`}>
                <img src={companionFileUrl(p.image_url)} alt={p.caption || ""} className="w-full aspect-square object-cover" loading="lazy" />
                <div className="absolute top-1 left-1 text-[10px] px-1.5 py-0.5 rounded bg-white/95 text-crest-navy font-medium">
                  {PHOTO_LABELS[p.label] || p.label}
                </div>
                {p.published_to_gallery && (
                  <div className="absolute top-1 right-1 text-[10px] px-1.5 py-0.5 rounded bg-crest-gold/95 text-crest-navy font-medium flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3" /> Public
                  </div>
                )}
                {(isAdmin || p.uploaded_by === user?.id) && (
                  <button
                    onClick={() => deletePhoto(p.id)}
                    className="absolute bottom-1 right-1 h-7 w-7 grid place-items-center rounded bg-white/95 text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-label="Delete photo"
                    data-testid={`delete-photo-${p.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
                {p.caption && <div className="px-2 py-1.5 text-[11px] text-crest-navy truncate">{p.caption}</div>}
              </div>
            ))}
          </div>
        )}
      </section>

      {isAdmin && (
        <button onClick={deleteJob} className="w-full mt-4 text-sm text-destructive hover:underline" data-testid="job-delete">
          Delete this job
        </button>
      )}
    </div>
  );
};

export default CompanionJobDetail;
